<template>
    <div>
        <Heading type="h2">{{ __('admin::labels.employments') }}</Heading>

        <FormError :message="errors.employments" />

        <div class="space-y-4 mt-2">
            <div
                v-for="(employment, index) in employments"
                :key="index"
                class="relative p-4 bg-gray-50 rounded shadow"
            >
                <button
                    type="button"
                    class="absolute -top-2 -right-2 rounded-full bg-red-500 p-1"
                    @click="deleteEmployment(index)"
                >
                    <XIcon class="w-4 h-4 text-white" />
                </button>

                <div class="text-lg font-bold mb-4">{{ __('admin::labels.employment') }} #{{ index + 1}}</div>

                <FormGroup :label="__('admin::labels.company')" :error="errors?.employments?.[index]?.company_id">
                    <Select :options="companyOptions" :has-error="exists(errors?.employments?.[index]?.company_id)" v-model="employment.company_id" :required="true" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.email')" :error="errors?.employments?.[index]?.email">
                    <Input type="email" :has-error="exists(errors?.employments?.[index]?.email)" v-model="employment.email" :required="true" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.work_phone')" :error="errors?.employments?.[index]?.phone">
                    <Input type="tel" :has-error="exists(errors?.employments?.[index]?.phone)" v-model="employment.phone" :required="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.organization_level')" :error="errors?.employments?.[index]?.organization_level">
                    <Select :options="organizationLevelOptions" :has-error="exists(errors?.employments?.[index]?.organization_level)" v-model="employment.organization_level" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.departments')" :error="errors?.employments?.[index]?.departments">
                    <Checkbox
                        v-for="(v, k) in departmentOptions"
                        :key="k"
                        :label="v"
                        :value="k"
                        :checked="employment.departments.includes(k)"
                        @update:checked="handleCheckedDepartment(index, k, $event)"
                    />
                </FormGroup>

                <Heading type="h2" class="my-2">{{ __('admin::labels.employee_information') }}</Heading>

                <FormGroup :label="__('admin::labels.job_title')" :error="errors?.employments?.[index]?.job_title">
                    <Input type="text" :has-error="exists(errors?.employments?.[index]?.job_title)" v-model="employment.job_title" :required="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.avatar')" :error="errors?.employments?.[index]?.avatar_id">
                    <FileSelectorInput :name="'avatar_' + index" :has-error="exists(errors?.employments?.[index]?.avatar_id)" v-model="employment.avatar_id" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.image')" :error="errors?.employments?.[index]?.image_id">
                    <FileSelectorInput :name="'image_' + index" :has-error="exists(errors?.employments?.[index]?.image_id)" v-model="employment.image_id" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.short_description')" :error="errors?.employments?.[index]?.short_description">
                    <Textarea :has-error="exists(errors?.employments?.[index]?.short_description)" v-model="employment.short_description" :required="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.long_description')" :error="errors?.employments?.[index]?.long_description">
                    <Textarea :has-error="exists(errors?.employments?.[index]?.long_description)" v-model="employment.long_description" :required="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.employed_at')" :error="errors?.employments?.[index]?.employed_at">
                    <Datepicker :has-error="exists(errors?.employments?.[index]?.employed_at)" v-model="employment.employed_at" :required="false" autocomplete="employment-date" />
                </FormGroup>

                <Heading type="h2" class="mt-2 mb-4">{{ __('admin::labels.settings') }}</Heading>

                <div class="mb-6 space-y-3">
                    <Toggle v-model:checked="employment.is_active" :label="__('admin::labels.is_active')" />
                    <Toggle v-model:checked="employment.sync_with_gmail" :label="__('admin::labels.sync_with_gmail')" v-if="employment.is_active" />
                    <Toggle v-model:checked="employment.sync_with_wordpress" :label="__('admin::labels.sync_with_wordpress')" v-if="employment.is_active" />
                    <Toggle v-model:checked="employment.use_autoreply" :label="__('admin::labels.use_autoreply')" />
                    <Toggle v-model:checked="employment.forward_email" :label="__('admin::labels.forward_email')" />
                </div>

                <div v-if="employment.use_autoreply">
                    <FormGroup :label="__('admin::labels.autoreply_subject')" :error="errors?.employments?.[index]?.autoreply_subject">
                        <Input type="text" :has-error="exists(errors?.employments?.[index]?.autoreply_subject)" v-model="employment.autoreply_subject" :required="false" />
                    </FormGroup>

                    <FormGroup :label="__('admin::labels.autoreply_body')" :error="errors?.employments?.[index]?.autoreply_body">
                        <MarkdownEditor v-model="employment.autoreply_body" />
                    </FormGroup>
                </div>

                <FormGroup :label="__('admin::labels.forward_email_to')" :error="errors?.employments?.[index]?.forward_email_to" v-if="employment.forward_email">
                    <Input type="email" :has-error="exists(errors?.employments?.[index]?.forward_email_to)" v-model="employment.forward_email_to" :required="false" />
                </FormGroup>

                <FormGroup :label="__('admin::labels.list_order')" :error="errors?.employments?.[index]?.list_order">
                    <Input name="list_order" type="number" step="1" :has-error="exists(errors?.employments?.[index]?.list_order)" v-model="employment.list_order" :required="false" />
                </FormGroup>

            </div>
        </div>

        <div class="relative mt-4 mb-8">
            <div class="absolute top-5 w-full h-[1px] bg-gray-100" />

            <div class="w-full flex justify-center relative">
                <button
                    type="button"
                    class="relative z-10 flex items-center gap-2 text-gray-600 px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200 transition-all"
                    @click="addEmployment"
                >
                    <PlusIcon class="w-5 h-5" />
                    <span>{{ __('admin::labels.add_block') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { PlusIcon, XIcon } from '@heroicons/vue/outline';
import FormError from "./FormError.vue";

export default {
    components: {
        FormError,
        PlusIcon,
        XIcon,
    },
    props: {
        modelValue: Array,
        errors: Object,
        organizationLevelOptions: Object,
        companyOptions: Object,
        departmentOptions: Object,
    },
    data() {
        return {
            employments: this.modelValue,
        }
    },
    watch: {
        employment: {
            handler: function (newValue) {
                this.$emit('update:modelValue', newValue);
            },
            deep: true,
        },
    },
    methods: {
        addEmployment() {
            this.employments.push({
                id: null,
                company_id: Object.keys(this.companyOptions)[0],
                email: '',
                phone: '',
                organization_level: Object.keys(this.organizationLevelOptions)[0],
                departments: [],
                job_title: '',
                avatar_id: null,
                image_id: null,
                short_description: '',
                long_description: '',
                employed_at: '',
                is_active: true,
                sync_with_gmail: true,
                sync_with_wordpress: true,
                use_autoreply: false,
                autoreply_subject: '',
                autoreply_body: '',
                forward_email: false,
                forward_email_to: '',
                list_order: null,
            });
        },
        deleteEmployment(index) {
            this.employments.splice(index, 1);
        },
        handleCheckedDepartment(employmentIndex, key, checked) {
            const index = this.employments[employmentIndex].departments.indexOf(key);

            if (checked) {
                if (index === -1) {
                    this.employments[employmentIndex].departments.push(key);
                }
            }
            else {
                if (index > -1) {
                    this.employments[employmentIndex].departments.splice(index, 1);
                }
            }
        },
    }
}
</script>
